import { useContext, useEffect, useState } from "react";
import { useModalAction } from "../modal-views/context";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { useIsMounted } from "src/lib/hooks/use-is-mounted";
import GlobalContext from "src/context/global-context";
import { ConversionTransaction, PaginationInput } from "src/api/types";
import {
  APISomethingWrongMsg,
  LIST_PAGE_SIZE,
  NetworkDisconnectMsg,
  permissions as permissionConstants,
} from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
import client from "../../api";
import { useMutation } from "react-query";
import {
  capitalizeFirstLetter,
  getErrorMessage,
  logger,
  makeNumberWithFloatingDigits,
} from "src/lib/utils";
import { ColumnsType } from "antd/es/table";
import { Table } from "antd";

const WalletTransactionsPage = ({
  searchText,

  searchField,
  startDate,
  endDate,
  isBuisiness,
  timezone,
}: {
  searchText: string;

  searchField: string;
  startDate?: Date | null;
  endDate?: Date | null;
  isBuisiness?: boolean;
  timezone: string;
}) => {
  const navigate = useNavigate();

  const targetModule = isBuisiness
    ? permissionConstants.TRANSACTION_HISTORY_BUSINESS
    : permissionConstants.TRANSACTION_HISTORY_INDIVIDUAL;
  const { canView, canList, canDownload } = usePermissions(targetModule);
  const canViewTransactions = canView || canList;

  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);
  const isMounted = useIsMounted();
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const { openModal } = useModalAction();

  const [dataSource, setDataSource] = useState<ConversionTransaction[]>([]);
  const [searchParams, setSearchParams] = useState<PaginationInput>({
    per_page: LIST_PAGE_SIZE,
    current_page: 1,
  });

  const [total, setTotal] = useState(0);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  useEffect(() => {
    updateWindowSize();
    window.addEventListener("resize", updateWindowSize);

    return () => window.removeEventListener("resize", updateWindowSize);
  }, []);

  function updateWindowSize() {
    setIsMobile(window.innerWidth < 768 ? true : false);
  }
  useEffect(() => {
    setSearchParams({
      ...searchParams,
      current_page: 1,
    });
  }, [searchText, startDate, endDate]);

  const onPageChange = (page: number, pageSize: number) => {
    logger.log(page, pageSize);
    setSearchParams({
      ...searchParams,
      current_page: page,
    });
  };
  const { mutate: downloadReceipt, isLoading: downloadReceiptLoading } =
    useMutation(client.transactions.downloadTransactionReceiptPdf, {
      onSuccess: (pdfBlob, variables) => {
        const blob = new Blob([pdfBlob], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `invoice_${variables.id}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();

        setAlertText("Receipt downloaded successfully");
        setIsAlertOpened(true);
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          setAlertText(
            error.response
              ? getErrorMessage(error.response.data)
              : APISomethingWrongMsg
          );
        }
        setIsAlertOpened(true);
      },
    });

  const receiptBtn = (record: any) => {
    if (!canDownload) {
      setAlertText("You do not have permission to download receipt");
      setIsAlertOpened(true);
      return;
    }
    logger.log("RECORD", record);
    setDownloadingId(record.id);
    downloadReceipt(
      {
        type: "wallet_to_wallet",
        id: record.id,
        timezone: timezone,
      },
      {
        onSettled: () => setDownloadingId(null),
      }
    );
  };

  const { mutate: getList, isLoading } = useMutation(
    isBuisiness
      ? client.walletToWallet.getWalletBusinessTransactionsList
      : client.walletToWallet.getWalletIndividualTransactionsList,
    {
      onSuccess: (data: any) => {
        const dataObj = data.data;
        setTotal(dataObj.total);
        setDataSource(
          dataObj?.data.map((item: any) => ({
            key: item.id,
            id: item.id,
            transaction_id: item.transaction_id,
            sender_id: item.sender.id,
            sender_email: item.sender.email,
            senderName: `${item.sender.user_data.first_name || "_"} ${
              item.sender.user_data.last_name || ""
            }`.trim(),
            receiver_id: item.receiver.id,
            receiver_email: item.receiver.email,
            receiverName: `${item.receiver.user_data.first_name || "_"} ${
              item.receiver.user_data.last_name || ""
            }`.trim(),
            indicated_amount: item.amount,
            fee: item.fee,
            amount: Number(item.fee) + Number(item.amount),
            currency: item.asset.code,
            status: item.status,
            description: item.note,
            created_at: item.created_at,
            updated_at: item.updated_at,
          }))
        );
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    }
  );

  useEffect(() => {
    if (!isMounted || !canViewTransactions) {
      return;
    }
    getList({
      ...searchParams,
      start_date: startDate
        ? moment(startDate).format("yyyy-MM-DD")
        : undefined,
      end_date: endDate ? moment(endDate).format("yyyy-MM-DD") : undefined,
      search_value: searchText?.length > 0 ? searchText : undefined,
      searchField: searchField?.length > 0 ? searchField : undefined,
      type: isBuisiness ? "business" : "individual",
      timezone: timezone,
    });
  }, [isMounted, canViewTransactions, startDate, endDate, searchParams.current_page, searchField]);

  useEffect(() => {
    if (!isMounted || !canViewTransactions) {
      return;
    }
    getList({
      ...searchParams,
      current_page: 1,
      start_date: startDate
        ? moment(startDate).format("yyyy-MM-DD")
        : undefined,
      end_date: endDate ? moment(endDate).format("yyyy-MM-DD") : undefined,
      search_value: searchText.length > 0 ? searchText : undefined,
      searchField: searchField?.length > 0 ? searchField : undefined,
      type: isBuisiness ? "business" : "individual",
      timezone: timezone,
    });
  }, [searchText, canViewTransactions]);

  const columns: ColumnsType<ConversionTransaction> = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          {/* {L.translate("TransactionHistory.time")} */}
          Transaction ID
        </p>
      ),
      dataIndex: "transaction_id",
      className: "w-auto",
      key: "transaction_id",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Sender Id
        </p>
      ),
      dataIndex: "sender_id",
      className: "w-auto",
      key: "sender_id",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Sender Email
        </p>
      ),
      dataIndex: "sender_email",
      className: "w-auto",
      key: "sender_email",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value ?? "__"}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Sender Name
        </p>
      ),
      dataIndex: "senderName",
      className: "w-auto",
      key: "senderName",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value ?? "__"}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Receiver Id
        </p>
      ),
      dataIndex: "receiver_id",
      className: "w-auto",
      key: "receiver_id",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Receiver Name
        </p>
      ),
      dataIndex: "receiverName",
      className: "w-auto",
      key: "receiverName",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value ?? "__"}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Receiver Email
        </p>
      ),
      dataIndex: "receiver_email",
      className: "w-auto",
      key: "receiver_email",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value ?? "__"}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Indicated Amount
        </p>
      ),
      dataIndex: "indicated_amount",
      className: "w-auto",
      key: "indicated_amount",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {makeNumberWithFloatingDigits(value, 2)}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Fee
        </p>
      ),
      dataIndex: "fee",
      className: "w-auto",
      key: "fee",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {makeNumberWithFloatingDigits(value, 2)}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Amount
        </p>
      ),
      dataIndex: "amount",
      className: "w-auto",
      key: "amount",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {makeNumberWithFloatingDigits(value, 2)}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Currency
        </p>
      ),
      dataIndex: "currency",
      className: "w-auto",
      key: "currency",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value.toUpperCase() ?? "__"}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Date
        </p>
      ),
      dataIndex: "created_at",
      className: "w-auto",
      key: "created_at",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {/* {moment.unix(value).format("YYYY-MM-DD HH:mm:ss")} */}
            {moment.utc(value).local().format("YYYY-MM-DD HH:mm:ss")}{" "}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          {/* {L.translate("TransactionHistory.status")} */}
          Status
        </p>
      ),
      dataIndex: "status",
      className: "w-auto",
      key: "status",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {/* {getPaymentStatusName(fetchStatus(value).toLowerCase())} */}
            {/* {value?.toLowerCase() === "ok" ? "Success" : "Failed"} */}
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Description
        </p>
      ),
      dataIndex: "description",
      className: "w-auto",
      key: "description",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value ?? "__"}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Receipt
        </p>
      ),
      dataIndex: "receipt",
      className: "w-auto",
      key: "receipt",
      render: (_, record) =>
        record.status?.toLowerCase() !== "completed" || !canDownload ? (
          <p className="text-gray-400 text-[12px] font-manrope italic">---</p>
        ) : downloadingId === Number(record.id) ? (
          <p className="text-gray-400 text-[12px] font-manrope italic">
            Downloading...
          </p>
        ) : (
          <a
            onClick={() => receiptBtn(record)}
            className="text-secondary underline hover:text-primary text-[12px] font-manrope font-medium cursor-pointer"
          >
            Receipt
          </a>
        ),
    },
  ];

  if (!canViewTransactions) {
    return (
      <div className="custom-scroll bg-white rounded-[12px] w-full p-6 text-center text-secondary">
        You do not have permission to view transactions.
      </div>
    );
  }

  return (
    <div className="custom-scroll bg-white rounded-[12px] w-full">
      <div className="p-4 relative">
        <Table
          loading={isLoading}
          showSorterTooltip={false}
          columns={isMobile ? columns : columns}
          dataSource={dataSource}
          pagination={{
            total,
            current: searchParams.current_page,
            onChange: onPageChange,
            position: ["bottomCenter"],
            defaultPageSize: LIST_PAGE_SIZE,
            showSizeChanger: false,
          }}
        />
      </div>
    </div>
  );
};

export default WalletTransactionsPage;
